/**
 * MoGo categorized collection — vanilla ES5. Rebuilds grid from <template> clones + variant tiles.
 * Variant titles: if " - Set " appears multiple times, Liquid uses the LAST segment for set number (see section).
 * Cards/variants without a match are hidden when any filter is active on the Stickers tab; All tab + empty filters shows everything.
 * Mode B (variant tiles): Dawn card-wrapper / card markup; variant-level price from JSON. No Judge.me or theme ratings on these tiles.
 */
(function () {
  var VARIANT_TILE_IMG_SIZES = '(min-width: 750px) 20vw, 50vw';

  function debounce(fn, waitMs) {
    var t = null;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, waitMs);
    };
  }

  function notifySectionLoaded(sectionId) {
    document.dispatchEvent(
      new CustomEvent('shopify:section:load', { detail: { sectionId: String(sectionId || '') } })
    );
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function safeJsonParse(text, label) {
    try {
      return JSON.parse(text);
    } catch (e) {
      console.warn('[mogo-categorized] invalid JSON (' + label + ')', e);
      return null;
    }
  }

  function getTabIds(data) {
    var ids = [];
    if (data.config && data.config.showAllTab) ids.push('all');
    var cats = Array.isArray(data.categories) ? data.categories : [];
    for (var i = 0; i < cats.length; i++) {
      if (cats[i].id) ids.push(String(cats[i].id));
    }
    return ids;
  }

  function resolveInitialTab(data) {
    var ids = getTabIds(data);
    if (!ids.length) {
      console.warn('[mogo-categorized] No tabs available — add category blocks or enable All tab.');
      return null;
    }
    var want = data.config && data.config.defaultTab != null ? String(data.config.defaultTab) : '';
    if (want && ids.indexOf(want) === -1) {
      console.warn('[mogo-categorized] defaultTab "' + want + '" not available; falling back to "' + ids[0] + '".');
      want = ids[0];
    } else if (!want) {
      want = ids[0];
    }
    return want;
  }

  function findCategory(data, slug) {
    var cats = Array.isArray(data.categories) ? data.categories : [];
    for (var i = 0; i < cats.length; i++) {
      if (cats[i].id === slug) return cats[i];
    }
    return null;
  }

  /** Product row from Liquid includes `available` — used to pin featured listings when in stock. */
  function isMogoProductAvailable(products, handle) {
    if (!handle || !products || !products.length) return false;
    for (var i = 0; i < products.length; i++) {
      if (products[i].handle === handle) {
        var a = products[i].available;
        if (a === false || a === 0) return false;
        return true;
      }
    }
    return false;
  }

  /** Order: promoted handles that are in-stock & in `handles`, first in config order; then the rest in original order. */
  function reorderMogoHandlesFeaturedFirst(handles, products, promotedHandles) {
    if (!handles || !handles.length) return handles;
    var ph = Array.isArray(promotedHandles) ? promotedHandles : [];
    if (!ph.length) return handles.slice();
    var front = [];
    var used = {};
    for (var pi = 0; pi < ph.length; pi++) {
      var h = String(ph[pi] || '').trim();
      if (!h) continue;
      if (handles.indexOf(h) === -1) continue;
      if (!isMogoProductAvailable(products, h)) continue;
      front.push(h);
      used[h] = true;
    }
    var out = front;
    for (var j = 0; j < handles.length; j++) {
      var hj = handles[j];
      if (used[hj]) continue;
      out.push(hj);
    }
    return out;
  }

  /** In-stock rows first (featured order among those), sold-out rows last — every tab uses the same grid path. */
  function reorderMogoHandlesForGrid(handles, products, promotedHandles) {
    if (!handles || !handles.length) return handles;
    var inStock = [];
    var soldOut = [];
    for (var i = 0; i < handles.length; i++) {
      var hi = handles[i];
      if (isMogoProductAvailable(products, hi)) inStock.push(hi);
      else soldOut.push(hi);
    }
    var top = reorderMogoHandlesFeaturedFirst(inStock.slice(), products, promotedHandles);
    return top.concat(soldOut);
  }

  function moveFocus(opts, delta) {
    if (!opts.length) return;
    var active = document.activeElement;
    var idx = opts.indexOf(active);
    if (idx < 0) idx = delta > 0 ? -1 : 0;
    idx = (idx + delta + opts.length) % opts.length;
    opts[idx].focus();
  }

  function closeDetails(details) {
    details.removeAttribute('open');
    var s = details.querySelector('summary');
    if (s) s.setAttribute('aria-expanded', 'false');
  }

  function selectDropdownOption(details, optionButton) {
    var val = optionButton.getAttribute('data-value') || '';
    var panel = optionButton.closest('[role="listbox"]');
    if (panel) {
      var opts = panel.querySelectorAll('[role="option"]');
      for (var i = 0; i < opts.length; i++) {
        opts[i].setAttribute('aria-selected', String(opts[i] === optionButton));
      }
    }
    var chosen = details.querySelector('[data-mogo-cat-chosen]');
    if (chosen) chosen.textContent = optionButton.textContent.trim();
    details.dataset.mogoCatValue = val;
    closeDetails(details);
    details.dispatchEvent(new CustomEvent('mogoCatFilterChange', { bubbles: true }));
  }

  function attachDropdownBehavior(details) {
    var summary = details.querySelector('summary');
    var panel = details.querySelector('[role="listbox"]');

    details.addEventListener('toggle', function () {
      var open = details.hasAttribute('open');
      if (summary) summary.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        document.querySelectorAll('[data-mogo-cat-dropdown][open]').forEach(function (other) {
          if (other !== details) closeDetails(other);
        });
        var sel = panel && panel.querySelector('[role="option"][aria-selected="true"]');
        window.requestAnimationFrame(function () {
          var first = sel || (panel && panel.querySelector('[role="option"]'));
          if (first && first.focus) first.focus();
        });
      }
    });

    details.addEventListener('keyup', function (e) {
      if (e.key === 'Escape') {
        closeDetails(details);
      }
    });

    if (!panel) return;

    panel.addEventListener('keydown', function (e) {
      var options = [];
      panel.querySelectorAll('[role="option"]').forEach(function (n) {
        options.push(n);
      });
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        moveFocus(options, 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        moveFocus(options, -1);
      } else if (e.key === 'Enter' || e.key === ' ') {
        var el = document.activeElement;
        if (el && el.getAttribute && el.getAttribute('role') === 'option') {
          e.preventDefault();
          selectDropdownOption(details, el);
        }
      }
    });

    panel.querySelectorAll('[role="option"]').forEach(function (btn) {
      btn.addEventListener('click', function (ev) {
        ev.preventDefault();
        selectDropdownOption(details, btn);
      });
    });
  }

  function readFilterState(filtersEl) {
    if (!filtersEl) {
      return { album: '', star: '', q: '' };
    }
    var albumD = filtersEl.querySelector('[data-mogo-cat-dim="album"]');
    var starD = filtersEl.querySelector('[data-mogo-cat-dim="star"]');
    var searchEl = filtersEl.querySelector('[data-mogo-cat-search]');
    return {
      album: (albumD && albumD.dataset && albumD.dataset.mogoCatValue ? albumD.dataset.mogoCatValue : '') || '',
      star: (starD && starD.dataset && starD.dataset.mogoCatValue ? starD.dataset.mogoCatValue : '') || '',
      q: searchEl ? String(searchEl.value || '').trim() : ''
    };
  }

  function filtersActiveStickerTab(st) {
    return Boolean(st.album || st.star || st.q.length);
  }

  function stickerMatches(stickersRow, album, star, qLow) {
    if (album && String(stickersRow.set) !== String(album)) return false;
    if (star && String(stickersRow.star) !== String(star)) return false;
    if (qLow) {
      var nm = String(stickersRow.name || '').toLowerCase();
      if (nm.indexOf(qLow) === -1) return false;
    }
    return true;
  }

  function sortStickers(rows) {
    rows.sort(function (a, b) {
      if (a.star !== b.star) return a.star - b.star;
      if (a.set !== b.set) return a.set - b.set;
      return String(a.name).localeCompare(String(b.name));
    });
  }

  /** Inline RGB from Liquid (gift_card-style color_schemes lookup) — fixes sale/sold badges when CSS vars inherit wrong. */
  function mogoRgbTriple(cfg, key) {
    if (!cfg || cfg[key] == null) return '';
    var v = String(cfg[key]).trim();
    return v.length ? v : '';
  }

  function mogoBadgeInlineStyle(cfg, tone) {
    var bgKey = tone === 'sale' ? 'saleBadgeBgRgb' : 'soldOutBadgeBgRgb';
    var fgKey = tone === 'sale' ? 'saleBadgeFgRgb' : 'soldOutBadgeFgRgb';
    var brKey = tone === 'sale' ? 'saleBadgeBorderRgb' : 'soldOutBadgeBorderRgb';
    var bg = mogoRgbTriple(cfg, bgKey);
    var fg = mogoRgbTriple(cfg, fgKey);
    var br = mogoRgbTriple(cfg, brKey) || fg;
    if (!bg || !fg) return '';
    return (
      ' style="background-color: rgb(' +
      bg +
      ');color: rgb(' +
      fg +
      ');border-color: rgba(' +
      br +
      ', 0.1)"'
    );
  }

  /**
   * Dawn-style .price markup for a single variant (no volume pricing / badges inside price).
   * money_price / money_compare are plain currency text from Liquid (| money | strip_html) — Shopify's money
   * filters return HTML spans; stripping avoids visible tags here because strings are escaped for innerHTML.
   */
  function buildVariantPriceHtml(row, strings) {
    var pr = strings && strings.priceRegularLabel ? strings.priceRegularLabel : '';
    var ps = strings && strings.priceSaleLabel ? strings.priceSaleLabel : '';
    var cls = ['price'];
    if (!row.available) cls.push('price--sold-out');
    if (row.on_sale && row.available) cls.push('price--on-sale');
    var mp = esc(row.money_price || '');
    var mc = esc(row.money_compare || '');
    return (
      '<div class="' +
      cls.join(' ') +
      '">' +
      '<div class="price__container">' +
      '<div class="price__regular">' +
      '<span class="visually-hidden visually-hidden--inline">' +
      esc(pr) +
      '</span>' +
      '<span class="price-item price-item--regular">' +
      mp +
      '</span>' +
      '</div>' +
      '<div class="price__sale">' +
      '<span class="visually-hidden visually-hidden--inline">' +
      esc(pr) +
      '</span>' +
      '<span><s class="price-item price-item--regular">' +
      mc +
      '</s></span>' +
      '<span class="visually-hidden visually-hidden--inline">' +
      esc(ps) +
      '</span>' +
      '<span class="price-item price-item--sale price-item--last">' +
      mp +
      '</span>' +
      '</div>' +
      '</div>' +
      '</div>'
    );
  }

  /** Sticker subtitle: emphasized album / star tier numbers plus star emoji for quick scan. */
  function buildStickerCaption(row, strings) {
    var subSep = strings.subtitleSeparator || ' · ';
    var setWord = (strings.captionSetWord != null ? String(strings.captionSetWord) : 'Set').trim() || 'Set';
    var starEmoji = strings.starEmoji != null ? String(strings.starEmoji) : '\u2b50';
    var album = row.set != null ? String(row.set) : '';
    var tier = row.star != null ? String(row.star) : '';

    return (
      '<p class="caption-large mogo-filter__caption">' +
      '<span class="mogo-filter__caption-album">' +
      '<span class="mogo-filter__caption-label">' +
      esc(setWord) +
      ' </span>' +
      '<span class="mogo-filter__caption-num">' +
      esc(album) +
      '</span>' +
      '</span>' +
      '<span class="mogo-filter__caption-sep">' +
      esc(subSep) +
      '</span>' +
      '<span class="mogo-filter__caption-tier">' +
      '<strong class="mogo-filter__caption-num mogo-filter__caption-tier-num">' +
      esc(tier) +
      '</strong>' +
      '<span class="mogo-filter__caption-star">&nbsp;' +
      starEmoji +
      '</span>' +
      '</span>' +
      '</p>'
    );
  }

  /**
   * Variant tile: Dawn card-wrapper structure; badge sits in inner .card__content like standard product cards.
   * Uses CLS-safe width/height and responsive srcset when Liquid provides them.
   */
  function buildStickerCard(sectionId, row, cfg, strings) {
    var cardStyle = (cfg.cardStyle || 'standard').toString();
    var colorScheme = (cfg.cardColorScheme || 'scheme-1').toString();
    var badgePos = (cfg.badgePosition || '').toString();
    var saleRaw = cfg.saleBadgeColorScheme != null ? String(cfg.saleBadgeColorScheme).trim() : '';
    var soldRaw = cfg.soldOutBadgeColorScheme != null ? String(cfg.soldOutBadgeColorScheme).trim() : '';
    var saleScheme = saleRaw || 'scheme-5';
    var soldScheme = soldRaw || 'scheme-5';
    var ratioNum = row.ratio_pct != null ? Number(row.ratio_pct) : 100;
    if (isNaN(ratioNum) || ratioNum <= 0) ratioNum = 100;
    var ratioPct = ratioNum + '%';
    var linkId = 'MogoStickerLink-' + sectionId + '-' + row.vid;
    var badgeId = 'MogoStickerBadge-' + sectionId + '-' + row.vid;

    var outerCardCls =
      'card card--' +
      esc(cardStyle) +
      ' card--media card--extend-height' +
      (cardStyle === 'card' ? ' color-' + esc(colorScheme) + ' gradient' : '');
    var innerCls =
      'card__inner' +
      (cardStyle === 'standard' ? ' color-' + esc(colorScheme) + ' gradient' : '') +
      ' ratio';

    var hasImg = !!(row.vimg && String(row.vimg).trim());
    var iw = row.vimg_w != null ? parseInt(row.vimg_w, 10) : 533;
    var ih = row.vimg_h != null ? parseInt(row.vimg_h, 10) : 533;
    if (isNaN(iw) || iw < 1) iw = 533;
    if (isNaN(ih) || ih < 1) ih = 533;

    var mediaHtml;
    if (hasImg) {
      var srcset = row.vimg_srcset ? String(row.vimg_srcset).trim() : '';
      mediaHtml =
        '<img src="' +
        esc(row.vimg) +
        '"' +
        (srcset
          ? ' srcset="' + esc(srcset) + '" sizes="' + esc(VARIANT_TILE_IMG_SIZES) + '"'
          : '') +
        ' alt="' +
        esc(row.name) +
        '" width="' +
        iw +
        '" height="' +
        ih +
        '" loading="lazy" class="motion-reduce">';
    } else {
      mediaHtml = strings.placeholderStickerSvg || '';
    }

    var badgeInner = '';
    if (!row.available) {
      badgeInner =
        '<span id="' +
        esc(badgeId) +
        '" class="badge badge--bottom-left color-' +
        esc(soldScheme) +
        '"' +
        mogoBadgeInlineStyle(cfg, 'sold') +
        '>' +
        esc(strings.soldOut || '') +
        '</span>';
    } else if (row.on_sale) {
      badgeInner =
        '<span id="' +
        esc(badgeId) +
        '" class="badge badge--bottom-left color-' +
        esc(saleScheme) +
        '"' +
        mogoBadgeInlineStyle(cfg, 'sale') +
        '>' +
        esc(strings.onSale || '') +
        '</span>';
    }

    var ariaLblParts = [linkId];
    if (badgeInner) ariaLblParts.push(badgeId);
    var ariaLbl = ariaLblParts.join(' ');

    var caption = buildStickerCaption(row, strings);

    /* Intentionally no Judge.me block or ratings (see Mode A card-product markup). */

    var priceHtml = buildVariantPriceHtml(row, strings);

    return (
      '<div class="card-wrapper product-card-wrapper underline-links-hover">' +
      '<div class="' +
      outerCardCls +
      '" style="--ratio-percent: ' +
      esc(ratioPct) +
      ';">' +
      '<div class="' +
      innerCls +
      '" style="--ratio-percent: ' +
      esc(ratioPct) +
      ';">' +
      '<div class="card__media">' +
      '<div class="media media--transparent media--hover-effect">' +
      mediaHtml +
      '</div>' +
      '</div>' +
      '<div class="card__content">' +
      '<div class="card__badge ' +
      esc(badgePos) +
      '">' +
      badgeInner +
      '</div>' +
      '</div>' +
      '</div>' +
      '<div class="card__content">' +
      '<div class="card__information">' +
      '<h3 class="card__heading h5" id="MogoStickerTitle-' +
      sectionId +
      '-' +
      row.vid +
      '">' +
      '<a href="' +
      esc(row.vurl || '#') +
      '" id="' +
      esc(linkId) +
      '" class="full-unstyled-link" aria-labelledby="' +
      ariaLbl +
      '">' +
      esc(row.name) +
      '</a>' +
      '</h3>' +
      '<div class="card-information">' +
      caption +
      priceHtml +
      '</div>' +
      '</div>' +
      '</div>' +
      '</div>' +
      '</div>'
    );
  }

  function applyTruncationTpl(tpl, shown, total) {
    return String(tpl || '')
      .split('{{ shown }}')
      .join(String(shown))
      .split('{{ total }}')
      .join(String(total));
  }

  function setTabAccessibility(tabs, activeId) {
    for (var i = 0; i < tabs.length; i++) {
      var slug = tabs[i].getAttribute('data-mogo-cat-tab') || '';
      var sel = slug === activeId;
      tabs[i].setAttribute('aria-selected', sel ? 'true' : 'false');
      tabs[i].tabIndex = sel ? 0 : -1;
      tabs[i].classList.toggle('mogo-cat__tab--active', sel);
    }
  }

  function scrollMoGoTabChipIntoRail(tabs, activeId) {
    if (!tabs || !tabs.length) return;
    var rail =
      tabs[0].closest && tabs[0].closest('.mogo-cat__tabs-scroll');
    if (!rail || rail.scrollWidth <= rail.clientWidth + 1) return;
    var chip = null;
    for (var j = 0; j < tabs.length; j++) {
      var id = tabs[j].getAttribute('data-mogo-cat-tab') || '';
      if (id === activeId) {
        chip = tabs[j];
        break;
      }
    }
    if (!chip) return;
    requestAnimationFrame(function () {
      try {
        chip.scrollIntoView({
          behavior: 'auto',
          block: 'nearest',
          inline: 'center'
        });
      } catch (e) {}
    });
  }

  function bindMoGoTabsRailSwipe(rail) {
    if (!rail || typeof window.PointerEvent === 'undefined') return;
    if (rail.getAttribute('data-mogo-tab-rail-swipe') === '1') return;
    rail.setAttribute('data-mogo-tab-rail-swipe', '1');

    var MOVE_GATE = 10;
    var active = false;
    var ptrId = null;
    var startClientX = 0;
    var startClientY = 0;
    var lastClientX = 0;
    var dragging = false;
    var suppressNextClick = false;

    rail.addEventListener(
      'click',
      function (ev) {
        if (!suppressNextClick) return;
        ev.preventDefault();
        ev.stopPropagation();
        suppressNextClick = false;
      },
      true
    );

    function grabEnd() {
      active = false;
      ptrId = null;
      dragging = false;
      rail.classList.remove('mogo-cat__tabs-scroll--grabbing');
    }

    rail.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      active = true;
      ptrId = e.pointerId;
      startClientX = e.clientX;
      startClientY = e.clientY;
      lastClientX = e.clientX;
      dragging = false;
      suppressNextClick = false;
    });

    rail.addEventListener(
      'pointermove',
      function (e) {
        if (!active || e.pointerId !== ptrId) return;
        var dx = e.clientX - startClientX;
        var dy = e.clientY - startClientY;

        if (!dragging) {
          if (Math.abs(dx) < MOVE_GATE && Math.abs(dy) < MOVE_GATE) return;
          if (Math.abs(dy) >= Math.abs(dx)) {
            grabEnd();
            return;
          }
          dragging = true;
          suppressNextClick = true;
          rail.classList.add('mogo-cat__tabs-scroll--grabbing');
          lastClientX = e.clientX;
          try {
            rail.setPointerCapture(ptrId);
          } catch (err) {}
          return;
        }

        var delta =
          window.getComputedStyle(rail).direction === 'rtl'
            ? e.clientX - lastClientX
            : lastClientX - e.clientX;
        rail.scrollLeft += delta;
        lastClientX = e.clientX;
        e.preventDefault();
      },
      { passive: false }
    );

    function releasePointer(e) {
      if (!active || ptrId == null || e.pointerId !== ptrId) return;
      if (dragging) {
        try {
          rail.releasePointerCapture(ptrId);
        } catch (err2) {}
      } else {
        suppressNextClick = false;
      }
      grabEnd();
    }

    rail.addEventListener('pointerup', releasePointer);
    rail.addEventListener('pointercancel', releasePointer);
  }

  function initRoot(root) {
    var jsonId = root.getAttribute('data-mogo-cat-json-id') || '';
    var gridId = root.getAttribute('data-mogo-cat-grid-id') || '';
    var sectionId = root.getAttribute('data-mogo-cat-section-id') || '';
    var jsonEl = jsonId ? document.getElementById(jsonId) : null;
    var grid = gridId ? document.getElementById(gridId) : null;
    var filtersEl = root.querySelector('[data-mogo-cat-filters]');
    var truncEl = root.querySelector('[data-mogo-cat-truncation]');

    if (!jsonEl || !grid) return;

    var data = safeJsonParse(jsonEl.textContent || '{}', jsonId);
    if (!data) return;

    var initialTab = resolveInitialTab(data);
    if (initialTab == null) return;

    var tabs = [];
    root.querySelectorAll('[data-mogo-cat-tab]').forEach(function (b) {
      tabs.push(b);
    });

    var tabsRail = root.querySelector('.mogo-cat__tabs-scroll');
    if (
      tabsRail &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 749px)').matches
    ) {
      bindMoGoTabsRailSwipe(tabsRail);
    }

    var products = Array.isArray(data.products) ? data.products : [];
    var stickers = Array.isArray(data.stickers) ? data.stickers : [];
    var config = data.config || {};
    var maxVar = typeof config.maxVariantResults === 'number' ? config.maxVariantResults : 200;
    var stickersSlug = config.stickersTabSlug ? String(config.stickersTabSlug) : '';
    var facetsOn = config.showStickerFilterFacets !== false;
    var searchOn = config.showStickerFilterSearch !== false;
    var sectionWantsFilterUi = facetsOn || searchOn;
    var strings = data.strings || {};

    var activeTab = initialTab;

    root.querySelectorAll('[data-mogo-cat-dropdown][data-mogo-cat-dim]').forEach(function (d) {
      d.dataset.mogoCatValue = '';
      attachDropdownBehavior(d);
    });

    function applyFiltersAndRender(resetTruncationNotice) {
      var cat = findCategory(data, activeTab);
      var isAll = activeTab === 'all';
      var showFilters =
        !!filtersEl &&
        sectionWantsFilterUi &&
        !!(stickersSlug && activeTab === stickersSlug) &&
        cat &&
        cat.showStickerFilter === true;

      if (filtersEl) {
        filtersEl.classList.toggle('mogo-cat__filters--hidden', !showFilters);
        filtersEl.setAttribute('aria-hidden', showFilters ? 'false' : 'true');
      }

      var st = readFilterState(filtersEl);
      var variantMode =
        showFilters && stickers.length > 0 && filtersActiveStickerTab(st);

      if (resetTruncationNotice && truncEl) {
        truncEl.hidden = true;
        truncEl.classList.add('mogo-cat__notice--hidden');
        truncEl.textContent = '';
      }

      var fragHtml = '';

      function pushEmpty(msg) {
        fragHtml +=
          '<li class="grid__item mogo-cat__grid-empty" style="grid-column: 1 / -1;"><p class="mogo-cat__muted">' +
          esc(msg) +
          '</p></li>';
      }

      function collectHandlesOrdered() {
        var handles = [];
        if (isAll) {
          for (var p = 0; p < products.length; p++) {
            handles.push(products[p].handle);
          }
          return handles;
        }
        if (!cat || !cat.productHandles) return handles;
        for (var i = 0; i < cat.productHandles.length; i++) {
          handles.push(cat.productHandles[i]);
        }
        return handles;
      }

      if (!variantMode || !showFilters) {
        var ord = collectHandlesOrdered();
        var phCfg = Array.isArray(config.promotedHandles) ? config.promotedHandles : [];
        ord = reorderMogoHandlesForGrid(ord, products, phCfg);
        if (!ord.length && !isAll) {
          pushEmpty(strings.emptyCategory || 'Empty');
        }
        for (var h = 0; h < ord.length; h++) {
          var handle = ord[h];
          var tmpl = document.getElementById('MogoCatCard-' + sectionId + '-' + handle);
          if (tmpl && tmpl.content && tmpl.tagName === 'TEMPLATE') {
            var node = tmpl.content.firstElementChild ? tmpl.content.firstElementChild.cloneNode(true) : null;
            if (node && node.outerHTML) fragHtml += node.outerHTML;
          }
        }
      } else {
        if (!stickers.length) {
          pushEmpty(strings.filtersPending || '');
        } else {
          var rows = [];
          var qLow = st.q.toLowerCase();
          for (var s = 0; s < stickers.length; s++) {
            var row = stickers[s];
            if (stickerMatches(row, String(st.album || ''), String(st.star || ''), qLow)) rows.push(row);
          }
          sortStickers(rows);
          var capCount = rows.length > maxVar ? maxVar : rows.length;
          for (var ri = 0; ri < capCount; ri++) {
            var vk = rows[ri];
            fragHtml +=
              '<li class="grid__item">' + buildStickerCard(sectionId, vk, config, strings) + '</li>';
          }
          if (rows.length > maxVar && truncEl && strings.showingTruncated) {
            truncEl.textContent = applyTruncationTpl(strings.showingTruncated, capCount, rows.length);
            truncEl.hidden = false;
            truncEl.classList.remove('mogo-cat__notice--hidden');
          }
          if (!rows.length) {
            pushEmpty(strings.noResults || 'No match');
          }
        }
      }

      grid.innerHTML = fragHtml;
      notifySectionLoaded(sectionId);
    }

    root.addEventListener('mogoCatFilterChange', function () {
      applyFiltersAndRender(true);
    });

    function activateTab(slug, focusTab) {
      activeTab = slug;
      setTabAccessibility(tabs, slug);
      for (var ti = 0; ti < tabs.length; ti++) {
        if (tabs[ti].getAttribute('data-mogo-cat-tab') === slug) {
          if (focusTab) tabs[ti].focus();
          break;
        }
      }
      scrollMoGoTabChipIntoRail(tabs, slug);
      applyFiltersAndRender(true);
    }

    var tablist = root.querySelector('[role="tablist"]');
    if (tablist) {
      tablist.addEventListener('keydown', function (e) {
        var tabEls = [];
        for (var i = 0; i < tabs.length; i++) tabEls.push(tabs[i]);
        var idx = tabEls.indexOf(document.activeElement);
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          idx = (idx + 1 + tabEls.length) % tabEls.length;
          activateTab(tabEls[idx].getAttribute('data-mogo-cat-tab') || '', true);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          idx = idx <= 0 ? tabEls.length - 1 : idx - 1;
          activateTab(tabEls[idx].getAttribute('data-mogo-cat-tab') || '', true);
        } else if (e.key === 'Home') {
          e.preventDefault();
          activateTab(tabEls[0].getAttribute('data-mogo-cat-tab') || '', true);
        } else if (e.key === 'End') {
          e.preventDefault();
          activateTab(tabEls[tabEls.length - 1].getAttribute('data-mogo-cat-tab') || '', true);
        }
      });
    }

    tabs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        activateTab(btn.getAttribute('data-mogo-cat-tab') || '', false);
      });
    });

    var searchEl = filtersEl ? filtersEl.querySelector('[data-mogo-cat-search]') : null;
    if (searchEl) {
      var debouncedApply = debounce(function () {
        applyFiltersAndRender(true);
      }, 150);
      searchEl.addEventListener('input', debouncedApply);
      searchEl.addEventListener('search', debouncedApply);
    }

    activateTab(initialTab, false);
  }

  function boot() {
    document.querySelectorAll('[data-mogo-cat-root]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
